(ns app.front.view.application-wizard.forms
  (:require [app.domain.form.model :as form-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.form.subs :as form-subs]
            [app.front.process.save-tracking :as save-tracking]
            [app.front.ui :as ui]
            [app.front.ui.floating-action-bar :as ui-fab]
            [app.front.util :as futil]
            [app.front.view.form :as form]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [re-frame.core-instrumented :as rf]))

(when ^boolean js/goog.DEBUG
  (rf/reg-event-fx ::fill-forms-with-dummy-data
    (fn [_ [_ application-id]]
      {:comm/dev-command {:command    :application/fill-forms
                          :data       {:id application-id}
                          :on-success [::entities/merge-entity-db]}}))
  (rf/reg-event-fx ::seed-form-ai-update
    (fn [_ [_ application-id]]
      {:comm/dev-command {:command    :application/seed-form-ai-update
                          :data       {:id application-id}
                          :on-success [::entities/merge-entity-db]}})))

(rf/reg-event-fx ::toggle-form
  (fn [{:keys [db]} [_ id]]
    (let [currently-expanded (::form-subs/expanded-form-id db)
          collapsing?        (= currently-expanded id)
          new-expanded       (when-not collapsing? id)]
      (cond-> {:db (-> db
                       (assoc ::form-subs/expanded-form-id new-expanded)
                       (assoc ::form-subs/current-form-id new-expanded))}
        collapsing?
        (assoc :fx [[:dispatch [::form-subs/snapshot-form-field-progress id]]])

        new-expanded
        (assoc :fx [[:dispatch [::form-subs/navigate-form {:form-id new-expanded}]]])))))

(rf/reg-event-fx ::expand-form
  (fn [{:keys [db]} [_ id]]
    {:db (-> db
             (assoc ::form-subs/expanded-form-id id)
             (assoc ::form-subs/current-form-id id))
     :fx [[:dispatch [::form-subs/navigate-form {:form-id id}]]]}))

(rf/reg-sub ::application
  :<- [::ws-subs/workspace]
  :-> workspace-model/workspace-target)

(rf/reg-sub ::step-completed?
  :<- [::application]
  :<- [::entities/context]
  :<- [::ff/enabled? :feature-flag.id/form-confirmation]
  :<- [::ws-subs/loading?]
  (fn [[application ctx form-confirmation? loading?] _]
    ;; The forms step is complete once every form has all its mandatory fields filled in.
    ;; Form filtering mirrors ::form-subs/application-forms (skip sign-off, require a data schema).
    (let [form-confirmation-active? (form-subs/form-confirmation-active? application form-confirmation?)
          forms-loaded?             (and application (not loading?))
          forms                     (->> (entities/find-entities ctx {:entity/type   :entity.type/form
                                                                      :entity/parent (:db/id application)})
                                         (remove #(-> % :form/schema :form.schema/type (= :form.schema.type/sign-off)))
                                         (filter (comp :form.schema/data-schema :form/schema)))]
      (and forms-loaded?
           (every? #(and (form-model/form-required-fields-set? %)
                         (or (not form-confirmation-active?)
                             (form-model/form-confirmation-satisfied? % nil)))
                   forms)))))

(defn- form-card [tr lang form]
  (let [form-id      (:db/id form)
        expanded?    (= (<sub [::form-subs/expanded-form-id]) form-id)
        status       (<sub [::form-subs/form-wizard-status-color-code form-id])
        status-color (case status
                       :attention "var(--status-attention)"
                       :warning "var(--status-warning)"
                       :success "var(--status-success)"
                       "var(--status-neutral-01)")
        missing?     (<sub [::form-subs/form-missing-mandatory-fields? form-id])
        progress     (<sub [::form-subs/form-field-progress form-id expanded?])]
    [:div.mb-4.bg-ui-white.rounded.border.border-solid.border-ui-dark-gray.min-w-0
     {:style {:border-left-width "6px"
              :border-left-color status-color}}
     [:div.flex.flex-row.items-start.leading-none.p-4.pl-3.cursor-pointer.hover:bg-ui-blue-10
      {:role          "button"
       :tab-index     0
       :aria-expanded expanded?
       :on-click      #(>evt [::toggle-form form-id])
       :on-key-down   (fn [e]
                        (when (futil/enter-press? e)
                          (>evt [::toggle-form form-id])))}
      [:div.flex.flex-col.pr-4.justify-start
       [cc/icon {:icon        (if expanded? i/chevron-up i/chevron-down)
                 :description ""
                 :aria-hidden true
                 :width       "12"}]]
      [:div.flex.flex-row.flex-1.justify-between.flex-wrap.gap-2.items-start
       [:div.flex.flex-col.flex-1.justify-start.leading-snug
        [:div.font-bold.py-0-5 (form-model/form-label form lang)]]
       [:div.flex.flex-row.items-center.gap-4
        (when (or missing? (= status :attention))
          [ui/small-pill {:kind (if missing? :warning :attention)}
           (if missing?
             (tr [:wizard/forms-required-fields-missing])
             (tr [:wizard/forms-confirmation-needed]))])
        (when progress
          [:div.text-right
           [:span.font-bold (:filled progress)]
           [:span.text-ui-night-sky-80.font-normal (str "/" (:total progress))]])]]]
     (when expanded?
       [:div.p-4.pt-0.pl-11.overflow-x-auto
        ;; Keyed by the AI fill generation: field inputs seed their local state from the entity
        ;; only on mount, so a NoVa fill must force a remount to show the new values.
        ^{:key (str form-id "-" (<sub [::form-subs/ai-fill-generation]))}
        [form/inline-form {:wizard? true
                           :form-id form-id}]])]))

(defn- form-autosave-status [tr form status]
  (case status
    :saving (cc/inline-loading {:description (tr [:saving])})
    :saved  [:span.inline-block.my-4 (tr [:changes-saved])]
    :failed [:span.inline-block.my-4.text-status-error (tr [:changes-not-saved])]
    :idle   [form/autosave-info {:tr tr :form form :disabled? false}]
    nil))

(defn- form-autosave-bar [tr expanded-form-id]
  (when expanded-form-id
    (let [form     (<sub [::entities/entity expanded-form-id])
          status   (<sub [::save-tracking/status])
          desktop? (<sub [::media-query/breakpoint-matches? :md])]
      (if desktop?
        [ui-fab/floating-action-bar {}
         [:div.flex-1]
         [:div.font-semibold.mx-8
          [form-autosave-status tr form status]]
         [:div.flex-1]]
        [:div.fixed.z-40.left-0.right-0.mx-4.rounded.bg-ui-white.border.border-ui-gray.border-solid.p-1.flex.justify-center.items-center.shadow-lg
         ;; Bottom offset for autosave bar on mobile, to avoid overlapping with the action button bar
         {:style {:bottom "5.5rem"}}
         [:div.font-semibold.mx-8
          [form-autosave-status tr form status]]]))))

(defn view [tr]
  (let [lang             (<sub [:language])
        application      (<sub [::application])
        forms            (<sub [::form-subs/application-forms application])
        loading?         (<sub [::ws-subs/loading?])
        expanded-form-id (<sub [::form-subs/expanded-form-id])]
    [:div.min-w-0.max-w-full
     (cond
       loading?
       [cc/structured-list-skeleton]

       (empty? forms)
       [:p.text-ui-night-sky-80 (tr [:wizard/forms-no-forms])]

       :else
       [:<>
        (futil/for-all [{form-id :db/id :as form} forms]
          ^{:key form-id}
          [form-card tr lang form])
        (when ^boolean js/goog.DEBUG
          [:div.mt-4.controls.flex.gap-4
           (cc/button {:kind             :secondary
                       :icon             i/heart
                       :icon-position    :right
                       :icon-description (tr [:fill-required-fields])
                       :class-name       "debug-button"
                       :on-click         #(>evt [::fill-forms-with-dummy-data (:db/id application)])}
                      (tr [:fill-required-fields]))
           (cc/button {:kind             :secondary
                       :icon             i/heart
                       :icon-position    :right
                       :icon-description (tr [:wizard/forms-seed-ai-update])
                       :class-name       "debug-button"
                       :on-click         #(>evt [::seed-form-ai-update (:db/id application)])}
                      (tr [:wizard/forms-seed-ai-update]))])
        [form-autosave-bar tr expanded-form-id]])]))
